// Section 04 — "Where things live". Three annotated screenshots of the
// real app (dashboard / forecast / review), served from
// frontend/public/tutorial/. They are captured AFTER the typography refresh
// lands; until then each frame degrades to a styled placeholder via onError.
// The numbered callouts overlay the frame in % coordinates, so they survive
// both the placeholder and the real image.

import React, { useState } from 'react';

const SHOTS = [
  {
    src: '/tutorial/dashboard.png',
    title: 'Budget dashboard',
    path: '/budget',
    blurb: 'Your “FY26 - Actual to Budget” sheet, permanently up to date.',
    callouts: [
      { n: 1, x: 14, y: 42, text: 'The owner → category → line tree, same order as the workbook.' },
      { n: 2, x: 56, y: 30, text: 'Budget / Actual / Committed / Variance per quarter — over-budget cells highlight themselves.' },
      { n: 3, x: 74, y: 62, text: 'Click any cell to open the rows behind it: SAP postings and committed invoices.' },
      { n: 4, x: 30, y: 12, text: 'Nothing hides: unassigned rows, missing FX and FY mismatches each get a visible bucket.' },
    ],
  },
  {
    src: '/tutorial/forecast.png',
    title: 'Forecast entry',
    path: '/budget/forecast',
    blurb: 'The five FP&A template tabs, as one live grid.',
    callouts: [
      { n: 1, x: 16, y: 38, text: 'One row per vendor + purpose — exactly like the template tabs.' },
      { n: 2, x: 55, y: 46, text: 'Twelve monthly inputs; quarters and annual totals derive as you type.' },
      { n: 3, x: 32, y: 84, text: 'Saving creates a snapshot — the history of every revision lives below the grid.' },
    ],
  },
  {
    src: '/tutorial/review.png',
    title: 'Review queue',
    path: '/budget/review',
    blurb: 'The daily driver: SAP rows that want a human decision.',
    callouts: [
      { n: 1, x: 38, y: 45, text: 'Unclassified and low-confidence rows, least confident first.' },
      { n: 2, x: 72, y: 40, text: 'The suggested budget line with its confidence — confirm or pick another.' },
      { n: 3, x: 73, y: 16, text: 'Every decision becomes a learned mapping, so the queue gets shorter over time.' },
    ],
  },
];

function Shot({ shot }) {
  const [failed, setFailed] = useState(false);
  return (
    <figure className="tut-shot">
      <div className="tut-shot-frame">
        {!failed ? (
          <img src={shot.src} alt={`${shot.title} screenshot`} onError={() => setFailed(true)} />
        ) : (
          <div className="tut-shot-ph">
            <span className="route code">{shot.path}</span>
            <span className="msg">Live screenshot — captured from the app</span>
          </div>
        )}
        {shot.callouts.map((c) => (
          <span
            key={c.n}
            className="tut-callout num"
            style={{ left: `${c.x}%`, top: `${c.y}%` }}
            aria-hidden="true"
          >
            {c.n}
          </span>
        ))}
      </div>
      <figcaption className="tut-shot-legend">
        <h4>{shot.title}</h4>
        <p className="blurb">{shot.blurb}</p>
        <ol>
          {shot.callouts.map((c) => (
            <li key={c.n}>
              <span className="n num">{c.n}</span>
              {c.text}
            </li>
          ))}
        </ol>
      </figcaption>
    </figure>
  );
}

export default function ScreenshotsSection() {
  return (
    <section id="tut-where" className="tut-section">
      <div className="tut-kicker">04 · A quick tour</div>
      <h2>Where things live</h2>
      <p className="tut-lead">
        Three screens cover the whole workflow: the dashboard for reading, the forecast grid for
        planning, and the review queue for the handful of rows that want a human.
      </p>
      {SHOTS.map((s) => (
        <Shot key={s.src} shot={s} />
      ))}
    </section>
  );
}
